import React, { useEffect, useState } from "react";
import productionApi from "../Service/productionApi";
import "./EmployeeProduction.css";

function EmployeeProduction() {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [activeProductions, setActiveProductions] = useState([]);
  const [resumedProductions, setResumedProductions] = useState([]);
  const [timerIntervals, setTimerIntervals] = useState({});
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);

  // Helper function to get token from localStorage
  const getToken = () => {
    return localStorage.getItem('sessionToken');
  };

  // Helper function to get tenantId from localStorage
  const getTenantId = () => {
    return localStorage.getItem('tenantId');
  };

  // Helper function to get user role from localStorage
  const getUserRole = () => {
    const storedRole = localStorage.getItem('role');
    const storedUserRole = localStorage.getItem('userRole');
    const role = storedRole || storedUserRole;
    
    if (role) {
      return role.toUpperCase();
    }
    
    const authoritiesString = localStorage.getItem('authorities');
    if (authoritiesString) {
      try {
        const authorities = JSON.parse(authoritiesString);
        if (authorities && authorities.includes("OWNER")) {
          return "OWNER";
        }
      } catch (err) {
        console.error("Error parsing authorities:", err);
      }
    }
    
    return "EMPLOYEE";
  };

  // Helper function to create authenticated config
  const getAuthenticatedConfig = (params = {}) => {
    const config = { params };
    
    const token = getToken();
    if (token) {
      config.headers = { 'Authorization': `Bearer ${token}` };
    }
    
    const tenantId = getTenantId();
    if (tenantId) {
      config.headers = { ...config.headers, 'X-TENANTID': tenantId };
    }
    
    return config;
  };

  // ================ NEW: POLLING FOR SYNC ================
  useEffect(() => {
    if (activeProductions.length === 0) return;
    
    const pollInterval = setInterval(async () => {
      // Sirf ACTIVE productions ko poll karo
      const activeProdIds = activeProductions
        .filter(p => p.status === "ACTIVE")
        .map(p => p.productionId);
      
      if (activeProdIds.length === 0) return;
      
      for (const prodId of activeProdIds) {
        try {
          // Backend se latest state lelo
          const response = await productionApi.get(
            `/production/${prodId}/elapsed-time`,
            getAuthenticatedConfig()
          );
          
          const backendData = response.data;
          
          setActiveProductions(prev => {
            const newProductions = [...prev];
            const prodIndex = newProductions.findIndex(p => p.productionId === prodId);
            
            if (prodIndex === -1) return prev;
            
            const prod = newProductions[prodIndex];
            let changed = false;
            
            // SIRF status update karo, timer MAT karo
            if (prod.status !== backendData.status) {
              prod.status = backendData.status;
              changed = true;
              
              // Agar production PAUSED ho gayi, timer band karo
              if (backendData.status === "PAUSED") {
                stopTimerForProduction(prodId);
              }
            }
            
            // Steps ke status update karo
            if (backendData.steps) {
              backendData.steps.forEach((backendStep, idx) => {
                if (prod.steps[idx] && prod.steps[idx].status !== backendStep.status) {
                  prod.steps[idx].status = backendStep.status;
                  changed = true;
                  
                  // Agar step IN_PROGRESS se hat gaya, timer band karo
                  if (prod.steps[idx].status !== "IN_PROGRESS") {
                    stopTimerForProduction(prodId);
                  }
                }
              });
            }
            
            return changed ? newProductions : prev;
          });
        } catch (err) {
          console.error(`Polling error for production ${prodId}:`, err);
        }
      }
    }, 2000); // Har 2 second mein poll
    
    return () => clearInterval(pollInterval);
  }, [activeProductions.length]); // Sirf tab change jab active count change ho

  // Load initial data
  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      try {
        const role = getUserRole();
        setUserRole(role);
        
        await fetchProducts();
        await fetchActiveProductions(role);
        await fetchResumedProductions(role);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadInitialData();
    
    return () => {
      Object.values(timerIntervals).forEach(timerData => {
        if (timerData && timerData.intervalId) {
          clearInterval(timerData.intervalId);
        }
      });
    };
  }, []);

  // Fetch products
  const fetchProducts = async () => {
    try {
      const response = await productionApi.get("/production/products");
      setProducts(response.data);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // Fetch ACTIVE productions
  const fetchActiveProductions = async (role = userRole) => {
    try {
      const config = getAuthenticatedConfig();
      
      let endpoint = role === "OWNER" 
        ? "/production/owner/active-productions" 
        : "/production/employee/active-productions";
      
      const response = await productionApi.get(endpoint, config);
      
      if (response.data && response.data.length > 0) {
        const productionsWithElapsedTime = await Promise.all(
          response.data.map(async (prod) => {
            try {
              const elapsedTimeResponse = await productionApi.get(
                `/production/${prod.id}/elapsed-time`,
                getAuthenticatedConfig()
              );
              const productionData = elapsedTimeResponse.data;
              
              let currentStepIndex = 0;
              for (let i = 0; i < (productionData.steps || []).length; i++) {
                if (productionData.steps[i].status !== "COMPLETED") {
                  currentStepIndex = i;
                  break;
                }
              }
              
              return {
                productionId: productionData.id,
                productId: productionData.productId,
                productName: productionData.productName,
                steps: (productionData.steps || []).map((step, idx) => ({
                  ...step,
                  stepId: step.id,
                  id: step.id,
                  timer: step.elapsedSeconds || 0,
                  index: idx
                })),
                currentStepIndex: currentStepIndex,
                employeeName: productionData.employeeName || "",
                totalQuantity: productionData.totalQuantity || "",
                companyName: productionData.companyName || "",
                isResumed: false,
                status: productionData.status,
                totalElapsedSeconds: productionData.totalElapsedSeconds || 0
              };
            } catch (err) {
              console.error(`Error fetching production ${prod.id}:`, err);
              return null;
            }
          })
        );
        
        const validProductions = productionsWithElapsedTime.filter(p => p !== null);
        setActiveProductions(validProductions);
        
        // Start timers for IN_PROGRESS steps
        validProductions.forEach((prod, index) => {
          const currentStep = prod.steps[prod.currentStepIndex];
          if (currentStep && currentStep.status === "IN_PROGRESS") {
            setTimeout(() => {
              startTimerForProduction(
                prod.productionId, 
                index,
                prod.currentStepIndex,
                currentStep.timer || 0
              );
            }, 100);
          }
        });
      } else {
        setActiveProductions([]);
      }
    } catch (err) {
      console.error("Error fetching ACTIVE productions:", err);
      setActiveProductions([]);
    }
  };

  // Fetch resumed productions
  const fetchResumedProductions = async (role = userRole) => {
    try {
      const config = getAuthenticatedConfig();
      
      let endpoint = role === "OWNER" 
        ? "/production/owner/resumed-productions" 
        : "/production/employee/resumed-productions";
      
      const response = await productionApi.get(endpoint, config);
      
      if (response.data && response.data.length > 0) {
        const productionsWithElapsedTime = await Promise.all(
          response.data.map(async (prod) => {
            try {
              const elapsedTimeResponse = await productionApi.get(
                `/production/${prod.id}/elapsed-time`,
                getAuthenticatedConfig()
              );
              const productionData = elapsedTimeResponse.data;
              
              return {
                id: prod.id,
                productionId: prod.id,
                productId: prod.productId,
                productName: prod.productName,
                steps: (productionData.steps || []).map(step => ({
                  ...step,
                  stepId: step.id,
                  id: step.id,
                  timer: step.elapsedSeconds || 0
                })),
                currentStepIndex: prod.currentStepIndex || 0,
                totalElapsedSeconds: productionData.totalElapsedSeconds || 0,
                employeeName: productionData.employeeName || "",
                totalQuantity: productionData.totalQuantity || "",
                companyName: productionData.companyName || ""
              };
            } catch (err) {
              console.error(`Error fetching resumed production ${prod.id}:`, err);
              return {
                ...prod,
                steps: (prod.steps || []).map(step => ({
                  ...step,
                  stepId: step.id,
                  id: step.id,
                  timer: step.elapsedSeconds || 0
                })),
                currentStepIndex: prod.currentStepIndex || 0,
                totalElapsedSeconds: prod.totalElapsedSeconds || 0
              };
            }
          })
        );
        
        setResumedProductions(productionsWithElapsedTime);
      } else {
        setResumedProductions([]);
      }
    } catch (err) {
      console.error("Error fetching resumed productions:", err);
      setResumedProductions([]);
    }
  };

  // Refresh all productions
  const refreshProductions = async () => {
    setLoading(true);
    try {
      await fetchActiveProductions();
      await fetchResumedProductions();
    } catch (error) {
      console.error("Error refreshing productions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Start new production
  const startProduction = async () => {
    if (!selectedProductId) {
      alert("Please select a product first!");
      return;
    }

    try {
      const config = getAuthenticatedConfig({ productId: selectedProductId });
      const response = await productionApi.post("/production/start-production", null, config);

      const productInfo = products.find((p) => p.id === selectedProductId);
      if (!productInfo) {
        alert("Selected product not found!");
        return;
      }

      const stepsRes = await productionApi.get(`/production/steps/${selectedProductId}`);
      
      const newProduction = {
        productionId: response.data.id,
        productId: selectedProductId,
        productName: productInfo.productName,
        steps: stepsRes.data.map((s, idx) => ({ 
          ...s,
          stepId: null,
          status: "NOT_STARTED",
          timer: 0,
          index: idx
        })),
        currentStepIndex: 0,
        employeeName: "",
        totalQuantity: "",
        companyName: "",
        isResumed: false,
        status: "ACTIVE",
        totalElapsedSeconds: 0
      };

      setActiveProductions((prev) => [...prev, newProduction]);
      setSelectedProductId(null);
    } catch (err) {
      console.error("Error starting production:", err);
      alert("Failed to start production. Please try again.");
    }
  };

  // Resume production
  const resumeProduction = async (index) => {
    const prod = resumedProductions[index];
    
    try {
      const productionResponse = await productionApi.get(
        `/production/${prod.id}/elapsed-time`,
        getAuthenticatedConfig()
      );
      const productionData = productionResponse.data;
      
      const productStepsRes = await productionApi.get(`/production/steps/${prod.productId}`);
      const allProductSteps = productStepsRes.data || [];
      
      const mergedSteps = allProductSteps.map((productStep, idx) => {
        const existingStepTime = (productionData.steps || []).find(
          stepTime => stepTime.stepName === productStep.stepName
        );
        
        if (existingStepTime) {
          return {
            ...productStep,
            stepId: existingStepTime.id,
            status: existingStepTime.status,
            timer: existingStepTime.elapsedSeconds || 0,
            index: idx
          };
        } else {
          return {
            ...productStep,
            stepId: null,
            status: "NOT_STARTED",
            timer: 0,
            index: idx
          };
        }
      });
      
      let currentStepIndex = 0;
      for (let i = 0; i < mergedSteps.length; i++) {
        if (mergedSteps[i].status !== "COMPLETED") {
          currentStepIndex = i;
          break;
        }
      }
      
      await productionApi.post(
        "/production/resume-production", 
        null, 
        getAuthenticatedConfig({ productionRecordId: prod.id })
      );

      const resumedProd = {
        productionId: prod.id,
        productId: prod.productId,
        productName: prod.productName,
        steps: mergedSteps,
        currentStepIndex: currentStepIndex,
        employeeName: productionData.employeeName || "",
        totalQuantity: productionData.totalQuantity || "",
        companyName: productionData.companyName || "",
        isResumed: true,
        status: "ACTIVE",
        totalElapsedSeconds: productionData.totalElapsedSeconds || 0
      };

      setActiveProductions(prev => [...prev, resumedProd]);
      setResumedProductions(prev => prev.filter((_, i) => i !== index));
      
      const currentStep = resumedProd.steps[currentStepIndex];
      if (currentStep && currentStep.status === "IN_PROGRESS") {
        setTimeout(() => {
          startTimerForProduction(
            resumedProd.productionId, 
            activeProductions.length,
            currentStepIndex,
            currentStep.timer || 0
          );
        }, 100);
      }
    } catch (err) {
      console.error("Error resuming production:", err);
      alert("Failed to resume production. Please try again.");
    }
  };

  // Start timer - PERFECT as it is
  const startTimerForProduction = (productionId, productionIndex, stepIndex, initialTimer = 0) => {
    stopTimerForProduction(productionId);
    
    const startTime = Date.now();
    let currentTimer = initialTimer;
    
    const intervalId = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      currentTimer = initialTimer + elapsedSeconds;
      
      setActiveProductions(prev => {
        const newProductions = [...prev];
        const prodIndex = newProductions.findIndex(p => p.productionId === productionId);
        
        if (prodIndex !== -1 && newProductions[prodIndex].steps[stepIndex]) {
          const step = newProductions[prodIndex].steps[stepIndex];
          if (step.status === "IN_PROGRESS") {
            step.timer = currentTimer;
            newProductions[prodIndex].totalElapsedSeconds = 
              (newProductions[prodIndex].totalElapsedSeconds || 0) + 1;
          }
        }
        
        return newProductions;
      });
    }, 1000);
    
    setTimerIntervals(prev => ({
      ...prev,
      [productionId]: {
        intervalId: intervalId,
        startTime: startTime,
        initialTimer: initialTimer,
        stepIndex: stepIndex
      }
    }));
  };

  // Stop timer
  const stopTimerForProduction = (productionId) => {
    if (timerIntervals[productionId]) {
      clearInterval(timerIntervals[productionId].intervalId);
      setTimerIntervals(prev => {
        const newIntervals = {...prev};
        delete newIntervals[productionId];
        return newIntervals;
      });
    }
  };

  // Start step
  const startStep = async (index) => {
    const prod = activeProductions[index];
    if (!prod) return;
    
    const currentStepIndex = prod.currentStepIndex;
    const currentStep = prod.steps[currentStepIndex];

    try {
      const config = getAuthenticatedConfig({
        productionRecordId: prod.productionId,
        stepName: currentStep.stepName
      });
      
      const response = await productionApi.post("/production/start-step", null, config);

      setActiveProductions(prev => {
        const newProductions = [...prev];
        const step = newProductions[index].steps[currentStepIndex];
        
        step.stepId = response.data.id;
        step.status = "IN_PROGRESS";
        step.timer = 0;
        
        return newProductions;
      });
      
      startTimerForProduction(prod.productionId, index, currentStepIndex, 0);
    } catch (err) {
      console.error("Error starting step:", err);
      alert("Failed to start step. Please try again.");
    }
  };

  // Resume step
  const resumeStep = async (index) => {
    const prod = activeProductions[index];
    if (!prod) return;
    
    const currentStepIndex = prod.currentStepIndex;
    const currentStep = prod.steps[currentStepIndex];
    
    if (!currentStep.stepId && currentStep.status === "PAUSED") {
      alert("Cannot resume step: missing step information.");
      return;
    }
    
    const synced = await syncProductionElapsedTime(prod.productionId);
    if (!synced) return;
    
    const updatedProd = activeProductions[index];
    const updatedCurrentStep = updatedProd.steps[currentStepIndex];
    
    if (updatedCurrentStep.stepId) {
      try {
        const config = getAuthenticatedConfig({ stepId: updatedCurrentStep.stepId });
        await productionApi.post("/production/resume-step", null, config);
      } catch (err) {
        console.error("Error calling resume-step:", err);
      }
    }
    
    setActiveProductions(prev => {
      const newProductions = [...prev];
      const step = newProductions[index].steps[currentStepIndex];
      step.status = "IN_PROGRESS";
      step.timer = updatedCurrentStep.timer || step.timer || 0;
      return newProductions;
    });
    
    startTimerForProduction(
      prod.productionId, 
      index, 
      currentStepIndex, 
      updatedCurrentStep.timer || 0
    );
  };

  // Pause step
  const pauseStep = async (index) => {
    const prod = activeProductions[index];
    if (!prod) return;
    
    const currentStepIndex = prod.currentStepIndex;
    const currentStep = prod.steps[currentStepIndex];
    
    if (currentStep.status !== "IN_PROGRESS") {
      alert("Step is not in progress!");
      return;
    }

    if (currentStep.stepId) {
      try {
        const config = getAuthenticatedConfig({ stepId: currentStep.stepId });
        await productionApi.post("/production/pause-step", null, config);
      } catch (err) {
        console.error("Error pausing step:", err);
      }
    }
    
    setActiveProductions(prev => {
      const newProductions = [...prev];
      const step = newProductions[index].steps[currentStepIndex];
      step.status = "PAUSED";
      stopTimerForProduction(prod.productionId);
      return newProductions;
    });
  };

  // Handle step action
  const handleStepAction = async (index) => {
    const prod = activeProductions[index];
    if (!prod) return;
    
    const currentStepIndex = prod.currentStepIndex;
    const currentStep = prod.steps[currentStepIndex];
    
    await syncProductionElapsedTime(prod.productionId);
    
    const updatedProd = activeProductions[index];
    const updatedCurrentStep = updatedProd.steps[currentStepIndex];
    
    if (updatedCurrentStep.status === "PAUSED") {
      await resumeStep(index);
    } 
    else if (updatedCurrentStep.status === "NOT_STARTED") {
      await startStep(index);
    }
    else if (updatedCurrentStep.status === "IN_PROGRESS") {
      if (!timerIntervals[prod.productionId]) {
        startTimerForProduction(
          prod.productionId, 
          index, 
          currentStepIndex, 
          updatedCurrentStep.timer || 0
        );
      }
    }
  };

  // Finish step
  const finishStep = async (index) => {
    const prod = activeProductions[index];
    if (!prod) return;
    
    const currentStepIndex = prod.currentStepIndex;
    const currentStep = prod.steps[currentStepIndex];
    
    if (currentStep.status !== "IN_PROGRESS") {
      alert("Step is not in progress!");
      return;
    }

    if (currentStep.stepId) {
      try {
        const config = getAuthenticatedConfig({ stepId: currentStep.stepId });
        await productionApi.post("/production/finish-step", null, config);
      } catch (err) {
        console.error("Error finishing step:", err);
      }
    }
    
    setActiveProductions(prev => {
      const newProductions = [...prev];
      const step = newProductions[index].steps[currentStepIndex];
      stopTimerForProduction(prod.productionId);
      step.status = "COMPLETED";
      return newProductions;
    });
  };

  // Next step
  const nextStep = (index) => {
    setActiveProductions(prev => {
      const newProductions = [...prev];
      const prod = newProductions[index];
      
      if (prod.steps[prod.currentStepIndex].status !== "COMPLETED") {
        return prev;
      }
      
      if (prod.currentStepIndex < prod.steps.length - 1) {
        stopTimerForProduction(prod.productionId);
        prod.currentStepIndex += 1;
        
        const nextStep = prod.steps[prod.currentStepIndex];
        
        if (nextStep.status === "IN_PROGRESS") {
          setTimeout(async () => {
            await syncProductionElapsedTime(prod.productionId);
            const updatedProd = newProductions[index];
            const updatedNextStep = updatedProd.steps[prod.currentStepIndex];
            startTimerForProduction(prod.productionId, index, prod.currentStepIndex, updatedNextStep.timer || 0);
          }, 100);
        }
      } else {
        alert("This is the last step! Finish the production instead.");
      }
      
      return newProductions;
    });
  };

  // ================ FIXED: PAUSE PRODUCTION ================
  const pauseProduction = async (index) => {
    const prod = activeProductions[index];
    
    if (!window.confirm("Pause this production?")) {
      return;
    }
    
    try {
      // 1. Backend ko pause karo
      const config = getAuthenticatedConfig({ productionRecordId: prod.productionId });
      await productionApi.post("/production/pause-production", null, config);
      
      // 2. Local timer band karo
      stopTimerForProduction(prod.productionId);
      
      // 3. Local state update karo - status PAUSED karo
      setActiveProductions(prev => {
        const newProductions = [...prev];
        const production = newProductions[index];
        
        production.steps.forEach(step => {
          if (step.status === "IN_PROGRESS") {
            step.status = "PAUSED";
          }
        });
        production.status = "PAUSED";
        
        return newProductions;
      });
      
      // 4. Paused production ko resumed list mein add karo
      const pausedProduction = { 
        id: prod.productionId,
        productionId: prod.productionId,
        productId: prod.productId,
        productName: prod.productName,
        steps: prod.steps.map(step => ({...step})),
        currentStepIndex: prod.currentStepIndex,
        totalElapsedSeconds: prod.totalElapsedSeconds,
        employeeName: prod.employeeName,
        totalQuantity: prod.totalQuantity,
        companyName: prod.companyName
      };
      
      // 5. Active se hatao, resumed mein add karo
      setActiveProductions(prev => prev.filter((_, i) => i !== index));
      setResumedProductions(prev => [...prev, pausedProduction]);
      
      // 6. Refresh karo
      setTimeout(() => {
        fetchResumedProductions();
      }, 500);
      
    } catch (err) {
      console.error("Error pausing production:", err);
      alert("Failed to pause production. Please try again.");
    }
  };

  // ================ FIXED: SYNC FUNCTION - TIMERS MAT KARO ================
  const syncProductionElapsedTime = async (productionId) => {
    try {
      const response = await productionApi.get(
        `/production/${productionId}/elapsed-time`,
        getAuthenticatedConfig()
      );
      const productionData = response.data;
      
      setActiveProductions(prev => {
        const newProductions = [...prev];
        const prodIndex = newProductions.findIndex(p => p.productionId === productionId);
        
        if (prodIndex !== -1) {
          const prod = newProductions[prodIndex];
          
          // SIRF status update karo, timer MAT karo
          if (productionData.steps) {
            productionData.steps.forEach((backendStep, idx) => {
              if (prod.steps[idx]) {
                // Timer MAT karo - yeh locally update ho raha hai
                // prod.steps[idx].timer = backendStep.elapsedSeconds || 0;
                
                // SIRF status update karo
                if (prod.steps[idx].status !== backendStep.status) {
                  prod.steps[idx].status = backendStep.status;
                }
              }
            });
          }
          
          // Total time bhi MAT karo jab production ACTIVE ho
          if (prod.status !== "ACTIVE") {
            prod.totalElapsedSeconds = productionData.totalElapsedSeconds || 0;
          }
        }
        
        return newProductions;
      });
      
      return true;
    } catch (err) {
      console.error(`Error syncing production ${productionId}:`, err);
      return false;
    }
  };

  // Sync all
  const syncAllElapsedTimes = async () => {
    const syncPromises = activeProductions.map(prod => 
      syncProductionElapsedTime(prod.productionId)
    );
    await Promise.all(syncPromises);
  };

  // Finish production
  const finishProduction = async (index) => {
    const prod = activeProductions[index];
    
    if (!prod.employeeName || prod.employeeName.trim() === "") {
      alert("Please enter employee name!");
      return;
    }
    
    if (!prod.totalQuantity || isNaN(prod.totalQuantity) || Number(prod.totalQuantity) <= 0) {
      alert("Please enter a valid quantity (greater than 0)!");
      return;
    }
    
    const allStepsCompleted = prod.steps.every(step => step.status === "COMPLETED");
    if (!allStepsCompleted) {
      alert("Please complete all steps before finishing production!");
      return;
    }
    
    try {
      const config = getAuthenticatedConfig({
        productionRecordId: prod.productionId,
        employeeName: prod.employeeName.trim(),
        quantity: Number(prod.totalQuantity),
        companyName: prod.companyName || ""
      });
      
      await productionApi.post("/production/finish-production", null, config);
      
      stopTimerForProduction(prod.productionId);
      setActiveProductions((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      console.error("Error finishing production:", err);
      alert("Failed to finish production. Please try again.");
    }
  };

  // Cancel production
  const cancelProduction = async (index, fromResumed = false) => {
    const prod = fromResumed ? resumedProductions[index] : activeProductions[index];
    
    if (!window.confirm("Are you sure you want to cancel this production?")) {
      return;
    }
    
    try {
      const config = getAuthenticatedConfig({ 
        productionRecordId: fromResumed ? prod.id : prod.productionId 
      });
      
      await productionApi.post("/production/cancel-production", null, config);
      
      if (fromResumed) {
        setResumedProductions(prev => prev.filter((_, i) => i !== index));
      } else {
        stopTimerForProduction(prod.productionId);
        setActiveProductions(prev => prev.filter((_, i) => i !== index));
      }
      
      refreshProductions();
    } catch (err) {
      console.error("Error cancelling production:", err);
      alert("Failed to cancel production. Please try again.");
    }
  };

  // Helper functions
  const formatStatus = (status) => {
    const statusMap = {
      "NOT_STARTED": "Not Started",
      "IN_PROGRESS": "In Progress", 
      "PAUSED": "Paused",
      "COMPLETED": "Completed"
    };
    return statusMap[status] || status;
  };

  const formatTimer = (seconds) => {
    const secs = Number(seconds) || 0;
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const remainingSeconds = secs % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const formatTotalElapsedTime = (seconds) => {
    const secs = Number(seconds) || 0;
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="employee-production-container">
        <h1>Employee Production {userRole && `(${userRole})`}</h1>
        <div className="loading-message">
          <p>Loading productions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-production-container">
      <h1>Employee Production {userRole && `(${userRole})`}</h1>
      
      {/* Debug Info Panel */}
      <div style={{ 
        background: "#f8f9fa", 
        padding: "10px", 
        marginBottom: "20px",
        border: "1px solid #dee2e6",
        borderRadius: "5px",
        fontSize: "14px"
      }}>
        <p><strong>🔍 DEBUG INFO</strong></p>
        <p><strong>Current Role:</strong> {userRole}</p>
        <p><strong>Active Productions:</strong> {activeProductions.length}</p>
        <p><strong>Resumed Productions:</strong> {resumedProductions.length}</p>
        <p><strong>Polling Sync:</strong> 🟢 Active (2s)</p>
        <button 
          onClick={refreshProductions}
          style={{ padding: "5px 10px", marginRight: "10px", fontSize: "12px" }}
        >
          Manual Refresh
        </button>
      </div>
      
      {/* Refresh button */}
      <div className="refresh-section">
        <button 
          className="refresh-btn"
          onClick={refreshProductions}
          disabled={loading}
        >
          🔄 Refresh Productions
        </button>
      </div>

      {/* Resumed Productions */}
      {resumedProductions.length > 0 && (
        <div className="resumed-productions-panel">
          <h3>⏸️ Paused Productions ({resumedProductions.length})</h3>
          {resumedProductions.map((prod, index) => (
            <div key={prod.id} className="resumed-production-card">
              <div className="resumed-info">
                <h4>{prod.productName}</h4>
                <p>Production ID: {prod.id}</p>
                <p>Current Step: {prod.currentStepIndex + 1} of {prod.steps?.length || 0}</p>
                <p>Total Time: {formatTotalElapsedTime(prod.totalElapsedSeconds)}</p>
              </div>
              <div className="resumed-actions">
                <button 
                  className="resume-btn"
                  onClick={() => resumeProduction(index)}
                >
                  Resume Production
                </button>
                <button 
                  className="cancel-btn"
                  onClick={() => cancelProduction(index, true)}
                >
                  Cancel Production
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Production Section */}
      <div className="new-production-section">
        <h3>Start New Production</h3>
        <div className="product-selection">
          <select 
            value={selectedProductId || ""} 
            onChange={(e) => setSelectedProductId(Number(e.target.value))}
            className="product-select"
          >
            <option value="">-- Select Product --</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.productName}
              </option>
            ))}
          </select>
          <button 
            className="start-production-btn" 
            onClick={startProduction}
            disabled={!selectedProductId || loading}
          >
            Start New Production
          </button>
        </div>
      </div>

      {/* Active Productions */}
      {activeProductions.length > 0 && (
        <div className="active-productions-section">
          <h3>🏭 Active Productions ({activeProductions.length})</h3>
          <div className="active-productions-grid">
            {activeProductions.map((prod, index) => (
              <div key={prod.productionId} className="production-card">
                {prod.isResumed && <div className="resumed-badge">🔄 RESUMED</div>}
                
                <div className="production-header">
                  <h4>{prod.productName}</h4>
                  <div className="production-header-details">
                    <p className="production-id">ID: {prod.productionId}</p>
                    <p className="production-status">Status: {prod.status}</p>
                    <p className="production-total-time">Total: {formatTotalElapsedTime(prod.totalElapsedSeconds)}</p>
                  </div>
                </div>
                
                {/* Form Inputs */}
                <div className="production-form">
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Company Name (Optional)"
                      value={prod.companyName}
                      onChange={(e) => {
                        const copy = [...activeProductions];
                        copy[index].companyName = e.target.value;
                        setActiveProductions(copy);
                      }}
                      className="company-input"
                    />
                  </div>
                  
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Employee Name *"
                      value={prod.employeeName}
                      onChange={(e) => {
                        const copy = [...activeProductions];
                        copy[index].employeeName = e.target.value;
                        setActiveProductions(copy);
                      }}
                      className="employee-input"
                      required
                    />
                    
                    <input
                      type="number"
                      placeholder="Total Quantity *"
                      value={prod.totalQuantity}
                      onChange={(e) => {
                        const copy = [...activeProductions];
                        copy[index].totalQuantity = e.target.value;
                        setActiveProductions(copy);
                      }}
                      className="quantity-input"
                      min="1"
                      required
                    />
                  </div>
                </div>

                {/* Steps Section */}
                {prod.steps.length > 0 && (
                  <div className="steps-section">
                    <div className="current-step-info">
                      <h5>
                        Step {prod.currentStepIndex + 1} of {prod.steps.length}: 
                        <span className="step-name"> {prod.steps[prod.currentStepIndex].stepName}</span>
                      </h5>
                      <div className="step-status-info">
                        <span className={`status-badge ${prod.steps[prod.currentStepIndex].status.toLowerCase()}`}>
                          {formatStatus(prod.steps[prod.currentStepIndex].status)}
                        </span>
                        <span className="timer-display">
                          ⏱️ {formatTimer(prod.steps[prod.currentStepIndex].timer)}
                        </span>
                      </div>
                    </div>

                    {/* Step Progress */}
                    <div className="step-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${((prod.currentStepIndex + 1) / prod.steps.length) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className="step-counter">
                        {prod.currentStepIndex + 1} / {prod.steps.length} Steps
                      </div>
                    </div>

                    {/* Step Controls */}
                    <div className="step-controls">
                      <button
                        className={`btn start-step-btn ${["IN_PROGRESS", "COMPLETED"].includes(prod.steps[prod.currentStepIndex].status) ? "disabled" : ""}`}
                        onClick={() => handleStepAction(index)}
                        disabled={["IN_PROGRESS", "COMPLETED"].includes(prod.steps[prod.currentStepIndex].status)}
                      >
                        {prod.steps[prod.currentStepIndex].status === "PAUSED" ? "Resume Step" : "Start Step"}
                      </button>
                      
                      <button
                        className={`btn pause-step-btn ${prod.steps[prod.currentStepIndex].status !== "IN_PROGRESS" ? "disabled" : ""}`}
                        onClick={() => pauseStep(index)}
                        disabled={prod.steps[prod.currentStepIndex].status !== "IN_PROGRESS"}
                      >
                        Pause Step
                      </button>
                      
                      <button
                        className={`btn finish-step-btn ${prod.steps[prod.currentStepIndex].status !== "IN_PROGRESS" ? "disabled" : ""}`}
                        onClick={() => finishStep(index)}
                        disabled={prod.steps[prod.currentStepIndex].status !== "IN_PROGRESS"}
                      >
                        Finish Step
                      </button>
                      
                      <button
                        className={`btn next-step-btn ${prod.steps[prod.currentStepIndex].status !== "COMPLETED" ? "disabled" : ""}`}
                        onClick={() => nextStep(index)}
                        disabled={prod.steps[prod.currentStepIndex].status !== "COMPLETED" || 
                                  prod.currentStepIndex === prod.steps.length - 1}
                      >
                        Next Step →
                      </button>
                    </div>

                    {/* Production Controls */}
                    <div className="production-controls">
                      <button
                        className="btn pause-production-btn"
                        onClick={() => pauseProduction(index)}
                      >
                        ⏸️ Pause Production
                      </button>
                      
                      <button
                        className="btn finish-production-btn"
                        onClick={() => finishProduction(index)}
                      >
                        ✅ Finish Production
                      </button>
                      
                      <button
                        className="btn cancel-production-btn"
                        onClick={() => cancelProduction(index, false)}
                      >
                        ❌ Cancel Production
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No active productions message */}
      {activeProductions.length === 0 && resumedProductions.length === 0 && !loading && (
        <div className="no-productions-message">
          <p>No active or paused productions found. Start a new one above!</p>
        </div>
      )}
    </div>
  );
}

export default EmployeeProduction;